import React from "react";
import { Box, Container, Text, SimpleGrid, Flex, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { HashLink } from "react-router-hash-link";

const MotionBox = motion(Box);

const basicFeatures = [
  "Complete study guides",
  "Limited MCQs",
  "Daily practice",
];

const proFeatures = [
  "Complete study guides",
  "Full MCQ bank",
  "Integrated concept-based practice",
  "All future updates",
];

const FeatureItem = ({ text, dark = false }) => (
  <Flex align="center" gap={3}>
    <Box
      w="20px"
      h="20px"
      borderRadius="50%"
      flexShrink={0}
      bg={dark ? "rgba(0,149,200,0.2)" : "#E9FFFF"}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Check size={11} color={dark ? "#0095C8" : "#016293"} strokeWidth={3} />
    </Box>
    <Text
      fontFamily="'Inter', sans-serif"
      fontSize="0.9375rem"
      color={dark ? "rgba(255,255,255,0.8)" : "#475569"}
      lineHeight="1.5"
    >
      {text}
    </Text>
  </Flex>
);

export const Pricing = () => {
  return (
    <Box id="pricing" bg="#FFFFFF" py={{ base: "64px", md: "96px" }}>
      <Container maxW="1200px" px={{ base: "5", md: "8" }}>

        <Flex direction="column" align="center" textAlign="center" mb={{ base: 12, md: 16 }}>
          <Text
            fontFamily="'Inter', sans-serif"
            fontSize="0.8125rem"
            fontWeight="600"
            letterSpacing="0.1em"
            textTransform="uppercase"
            color="#0095C8"
            mb={3}
          >
            Pricing
          </Text>
          <Text
            as="h2"
            fontFamily="'Public Sans', sans-serif"
            fontWeight="600"
            fontSize={{ base: "1.75rem", md: "2.375rem" }}
            color="#293452"
            lineHeight="1.25"
            mb={4}
          >
            Focused preparation.{" "}
            <Box as="span" color="#0095C8">Simple access.</Box>
          </Text>
          <Box
            px={4}
            py={2}
            borderRadius="8px"
            bg="#fffbeb"
            border="1px solid #fde68a"
          >
            <Text
              fontFamily="'Inter', sans-serif"
              fontSize="0.875rem"
              color="#92400e"
              fontStyle="italic"
            >
              Early access pricing currently available
            </Text>
          </Box>
        </Flex>

        <SimpleGrid
          columns={{ base: 1, md: 2 }}
          spacing={{ base: 6, md: 8 }}
          maxW="760px"
          mx="auto"
        >

          {/* Basic */}
          <MotionBox
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45 }}
          >
            <Box
              p={{ base: 7, md: 8 }}
              bg="white"
              borderRadius="16px"
              border="1px solid rgba(0,149,200,0.15)"
              borderTop="4px solid #0095C8"
              boxShadow="0 2px 16px rgba(41,52,82,0.06)"
              h="full"
              display="flex"
              flexDirection="column"
            >
              <VStack align="stretch" spacing={6} flex={1}>
                <Box>
                  <Text
                    fontFamily="'Public Sans', sans-serif"
                    fontWeight="700"
                    fontSize="1.375rem"
                    color="#293452"
                    mb={3}
                  >
                    Basic
                  </Text>
                  <Flex align="baseline" gap={1}>
                    <Text fontFamily="'Inter', sans-serif" fontSize="1.5rem" color="#293452" fontWeight="600">₹</Text>
                    <Text
                      fontFamily="'Public Sans', sans-serif"
                      fontSize="3.25rem"
                      fontWeight="800"
                      color="#293452"
                      lineHeight="1"
                    >
                      1999
                    </Text>
                    <Text fontFamily="'Inter', sans-serif" color="rgba(41,52,82,0.5)" fontSize="0.9rem" pb={1}>/year</Text>
                  </Flex>
                </Box>

                <VStack align="stretch" spacing={3}>
                  {basicFeatures.map((f) => (
                    <FeatureItem key={f} text={f} />
                  ))}
                </VStack>
              </VStack>

              <Box mt={8}>
                <HashLink smooth to="/#contact">
                  <Box
                    as="button"
                    w="full"
                    py="13px"
                    borderRadius="8px"
                    border="2px solid #0095C8"
                    color="#0095C8"
                    fontFamily="'Public Sans', sans-serif"
                    fontWeight="600"
                    fontSize="0.9375rem"
                    letterSpacing="0.02em"
                    _hover={{ bg: "#0095C8", color: "white" }}
                    transition="all 0.25s ease"
                  >
                    Get Basic
                  </Box>
                </HashLink>
              </Box>
            </Box>
          </MotionBox>

          {/* Pro */}
          <MotionBox
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <Box
              p={{ base: 7, md: 8 }}
              bg="#293452"
              borderRadius="16px"
              border="2px solid rgba(0,149,200,0.4)"
              boxShadow="0 16px 48px rgba(0,149,200,0.18)"
              h="full"
              display="flex"
              flexDirection="column"
              position="relative"
              overflow="hidden"
            >
              {/* Top gradient border */}
              <Box
                position="absolute"
                top="0" left="0" right="0"
                h="4px"
                bgGradient="linear(to-r, #0095C8, #809CFF)"
              />

              {/* Decorative circle */}
              <Box
                position="absolute"
                top="-50px"
                right="-50px"
                w="160px"
                h="160px"
                borderRadius="50%"
                border="30px solid rgba(0,149,200,0.12)"
                pointerEvents="none"
              />

              <VStack align="stretch" spacing={6} flex={1} position="relative" zIndex={1}>
                <Box>
                  <Flex align="center" justify="space-between" mb={3}>
                    <Text
                      fontFamily="'Public Sans', sans-serif"
                      fontWeight="700"
                      fontSize="1.375rem"
                      color="white"
                    >
                      Pro
                    </Text>
                    <Box
                      px={3}
                      py={1}
                      borderRadius="full"
                      bgGradient="linear(135deg, #809CFF, #937CE6)"
                      boxShadow="0 2px 8px rgba(128,156,255,0.3)"
                    >
                      <Text
                        fontFamily="'Inter', sans-serif"
                        fontSize="0.75rem"
                        fontWeight="700"
                        color="white"
                        letterSpacing="0.03em"
                      >
                        Most Chosen
                      </Text>
                    </Box>
                  </Flex>
                  <Flex align="baseline" gap={1}>
                    <Text fontFamily="'Inter', sans-serif" fontSize="1.5rem" color="white" fontWeight="600">₹</Text>
                    <Text
                      fontFamily="'Public Sans', sans-serif"
                      fontSize="3.25rem"
                      fontWeight="800"
                      color="white"
                      lineHeight="1"
                    >
                      3999
                    </Text>
                    <Text fontFamily="'Inter', sans-serif" color="rgba(255,255,255,0.5)" fontSize="0.9rem" pb={1}>/year</Text>
                  </Flex>
                </Box>

                <VStack align="stretch" spacing={3}>
                  {proFeatures.map((f) => (
                    <FeatureItem key={f} text={f} dark />
                  ))}
                </VStack>
              </VStack>

              <Box mt={8} position="relative" zIndex={1}>
                <HashLink smooth to="/#contact">
                  <Box
                    as="button"
                    w="full"
                    py="13px"
                    borderRadius="8px"
                    background="linear-gradient(135deg, #0095C8, #016293)"
                    color="white"
                    fontFamily="'Public Sans', sans-serif"
                    fontWeight="600"
                    fontSize="0.9375rem"
                    letterSpacing="0.02em"
                    boxShadow="0 4px 16px rgba(0,149,200,0.35)"
                    _hover={{
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 24px rgba(0,149,200,0.45)",
                    }}
                    transition="all 0.25s cubic-bezier(0.4,0,0.2,1)"
                  >
                    Get Pro
                  </Box>
                </HashLink>
              </Box>
            </Box>
          </MotionBox>

        </SimpleGrid>
      </Container>
    </Box>
  );
};
